// Plain-language failures + bounded retry for server and API calls.
export interface CallFault { status: number | null; message: string; retryable: boolean }

export function describeFault(err: unknown): CallFault {
  const any = err as { status?: number; response?: { status?: number }; message?: string } | null;
  const status = any?.status ?? any?.response?.status ?? null;
  const raw = String(any?.message ?? err ?? "");
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  if (offline || /failed to fetch|networkerror|load failed|network/i.test(raw))
    return { status, message: "No signal — check your connection and tap Retry.", retryable: true };
  if (status === 401) return { status, message: "You need to sign in to do this.", retryable: false };
  if (status === 402) return { status, message: "AI credits have run out — Sophia's offline brain still works.", retryable: false };
  if (status === 403) return { status, message: "This isn't allowed from here.", retryable: false };
  if (status === 404) return { status, message: "That couldn't be found.", retryable: false };
  if (status === 429) return { status, message: "Sophia is busy — try again in a moment.", retryable: true };
  if (status && status >= 500) return { status, message: "The server hiccupped — tap Retry.", retryable: true };
  return { status, message: "Something went wrong — tap Retry.", retryable: true };
}

/** Run fn, retrying only temporary faults, max `tries` attempts with backoff. */
export async function withRetry<T>(fn: () => Promise<T>, tries = 3): Promise<T> {
  let last: unknown;
  for (let i = 0; i < tries; i++) {
    try { return await fn(); } catch (e) {
      last = e;
      if (!describeFault(e).retryable || i === tries - 1) break;
      await new Promise((r) => setTimeout(r, 800 * 2 ** i + Math.random() * 300));
    }
  }
  throw last;
}
